import type { Mentionable, TeamTask } from "@codvero/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { teamApi } from "@/api/studio.ts";
import { cn } from "@/lib/cn.ts";
import { AgentAvatar } from "@/ui/AgentAvatar.tsx";

export type AgentActivity = "ready" | "working" | "waiting";

/** What each teammate is doing in this chat, from its team tasks (polled while any is open). */
export function useTeamActivity(chatId: string | null | undefined): Record<string, AgentActivity> {
  const q = useQuery({
    queryKey: ["team", "tasks", chatId],
    queryFn: () => teamApi.tasks(chatId!),
    enabled: !!chatId,
    refetchInterval: (query) => ((query.state.data as TeamTask[] | undefined)?.some((x) => x.status === "RUNNING" || x.status === "PENDING_APPROVAL" || x.status === "QUEUED") ? 3000 : 15_000),
  });
  const out: Record<string, AgentActivity> = {};
  for (const task of q.data ?? []) {
    if (task.status === "RUNNING") out[task.agentHandle] = "working";
    else if ((task.status === "PENDING_APPROVAL" || task.status === "QUEUED") && out[task.agentHandle] !== "working") out[task.agentHandle] = "waiting";
  }
  return out;
}

export function ActivityChip({ state }: { state: AgentActivity }) {
  const { t } = useTranslation();
  return (
    <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", state === "working" ? "bg-accent-soft text-accent" : state === "waiting" ? "bg-warn-soft text-warn" : "bg-surface-2 text-fg-muted")}>
      {state === "working" && <span className="me-1 inline-block size-1.5 animate-pulse rounded-full bg-current align-middle" />}
      {t(`team.activity.${state}`)}
    </span>
  );
}

/**
 * The project team for a chat: "Entire team", groups, then agents. Tapping a row mentions it in the composer;
 * the checkbox pins an agent so every message goes to it (one pin at a time; ticking it again unpins).
 */
export function TeamRoster({ members, pinned, activity, canPin, onMention, onTogglePin }: { members: Mentionable[]; pinned: string | null; activity: Record<string, AgentActivity>; canPin: boolean; onMention: (handle: string) => void; onTogglePin: (handle: string | null) => void }) {
  const { t } = useTranslation();
  const all = members.find((m) => m.kind === "all");
  const groups = members.filter((m) => m.kind === "group");
  const agents = members.filter((m) => m.kind === "agent");
  return (
    <div className="space-y-2">
      {all && (
        <button type="button" onClick={() => onMention(all.handle)} className="flex w-full items-center gap-3 rounded-xl border border-accent/40 bg-accent-soft/30 px-3 py-3 text-start hover:border-accent">
          <span className="grid size-10 place-items-center rounded-full bg-accent-soft"><Users className="size-4 text-accent" /></span>
          <span className="flex-1 font-medium text-accent">{t("team.entire")}</span>
          <span dir="ltr" className="font-mono text-xs text-accent">@{all.handle}</span>
        </button>
      )}
      {groups.map((g) => (
        <button key={g.id} type="button" onClick={() => onMention(g.handle)} className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-start hover:border-accent/60">
          <span className="grid size-10 place-items-center rounded-full bg-surface-2"><Users className="size-4 text-fg-muted" /></span>
          <span className="flex-1 font-medium">{g.name}</span>
          <span dir="ltr" className="font-mono text-xs text-fg-muted">@{g.handle}</span>
        </button>
      ))}
      {agents.map((a) => {
        const on = pinned === a.handle;
        return (
          // a div, not a button: the pin checkbox inside it is its own control
          <div key={a.id} className={cn("flex w-full items-center gap-3 rounded-xl border bg-surface px-3 py-2.5", on ? "border-accent/60 bg-accent-soft/20" : "border-border")} style={{ borderInlineStartWidth: 3, borderInlineStartColor: a.avatar?.color }}>
            <label className="grid size-8 shrink-0 cursor-pointer place-items-center" title={on ? t("team.unpin") : t("team.pin")}>
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={on} disabled={!canPin} aria-label={on ? t("team.unpin") : t("team.pin")} onChange={() => onTogglePin(on ? null : a.handle)} />
            </label>
            <button type="button" onClick={() => onMention(a.handle)} className="flex min-w-0 flex-1 items-center gap-3 text-start">
              <AgentAvatar avatar={a.avatar} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{a.name}</span>
                <span dir="ltr" className="block truncate font-mono text-xs text-fg-muted">@{a.handle}</span>
              </span>
            </button>
            <ActivityChip state={activity[a.handle] ?? "ready"} />
          </div>
        );
      })}
      {agents.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-fg-muted">
          {t("team.empty")} <Link to="/agents" className="text-accent underline">{t("nav.agents")}</Link>
        </div>
      )}
    </div>
  );
}

/** Takes the place of the model controls while the chat is pinned to an agent: the agent runs on its own profile. */
export function PinnedAgentBar({ agent, state, onUnpin, disabled }: { agent: Mentionable; state: AgentActivity; onUnpin: () => void; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-accent/40 bg-accent-soft/20 ps-1.5 pe-1.5 md:h-10" title={t("team.pinnedHint", { name: agent.name })}>
      <AgentAvatar avatar={agent.avatar} size="sm" />
      <span className="min-w-0 flex-1 leading-tight">
        <b className="block truncate text-xs">{agent.name}</b>
        <small dir="ltr" className="block truncate font-mono text-[10.5px] text-fg-muted">@{agent.handle}</small>
      </span>
      <ActivityChip state={state} />
      <button type="button" disabled={disabled} onClick={onUnpin} className="shrink-0 rounded-lg border border-border bg-surface-2/60 px-2 py-1 text-[11px] hover:bg-surface-2 disabled:opacity-50">{t("team.unpin")}</button>
    </div>
  );
}
